/** @jsxImportSource @emotion/react */
import * as React from 'react';
import { css, SerializedStyles } from '@emotion/react';
import FloatingContent from '../FloatingContent';
import { isEndSide, isVerticalSide, toHorizPosition, toVertPosition } from '../position/position';
import { Side } from '../position/side';
import Arrow from '../../shapes/Arrow';
import { CLICKABLE } from '../../styles/styles';
import { BLACK } from '../../styles/colors';

const ToolTipContent = (flexDirection: string) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: ${flexDirection};
`

/**
 * TooltipView: a special-case component that shows floating content rendered inside a tooltip while surfacing through
 * props full control over that content. Very useful for e.g. hooking a timer up to a tooltip or whatever other crazy
 * logic we want to use that isn't just "show this thing in a tooltip and trigger on click/hover".
 *
 * PureComponent: true
 * Connected to Redux: false
 */
export default class TooltipView extends React.PureComponent<{
  showTooltip: boolean;
  side: Side;
  arrowColor: string;
  tooltipContent: React.ReactNode;
  children: React.ReactNode;
  activateOnHover: boolean;
  rootCss?: SerializedStyles;
  floatingContentCss?: SerializedStyles;
  onClickOutside: () => void;
  onClickInside: (event) => void;
  onHoverInside?: () => void;
  onHoverOutside?: () => void;
}, {}> {
  public static defaultProps = {
    showTooltip:     false,
    side:            'top',
    arrowColor:      BLACK,
    activateOnHover: false,
  };

  public flexDirectionFor = (
    side: Side,
  ): string => (isVerticalSide(side) ? 'column' : 'row') + (isEndSide(side) ? '-reverse' : '');


  public render = () => (
    <FloatingContent rootCss={this.props.floatingContentCss}
                     showFloatingContent={this.props.showTooltip}
                     trackMouse={this.props.activateOnHover}
                     xPosition={toHorizPosition(this.props.side)}
                     yPosition={toVertPosition(this.props.side)}
                     onHoverInside={this.props.onHoverInside}
                     onHoverOutside={this.props.onHoverOutside}
                     onClickOutside={this.props.onClickOutside}
                     floatingContent={
                       // Tooltip Content
                       <div css={ToolTipContent(this.flexDirectionFor(this.props.side))}>
                         {this.props.tooltipContent}
                         <Arrow color={this.props.arrowColor} side={this.props.side} sizePx={5}/>
                       </div>
                     }>

      {/* Anchor Content */}
      <div onClick={this.props.onClickInside}
           css={css`
             ${CLICKABLE};
             ${this.props.rootCss};
           `}>
        {this.props.children}
      </div>
    </FloatingContent>
  );
}
